import type { VisitorStorage } from "./visitor-storage";

/**
 * Local read watermark per thread: the time of the last message the visitor has read, kept in visitor
 * storage. The BE unread count can come back stale (the read-ack was rejected, e.g. for a blocked
 * visitor, or is still queued): a refetch or a stream reconnect then re-raised the launcher peek, the
 * badge and the "Unread messages" divider for a message already read. A thread whose last message is
 * at or before its mark is read, whatever the BE count says; a NEWER message is unread again.
 */
export type ReadMarks = Readonly<Record<string, number>>;

const PREFIX = (publicKey: string | null) => `wexio:read-mark:${publicKey ?? ""}:`;
export const readMarkKey = (publicKey: string | null, chatId: string): string =>
  `${PREFIX(publicKey)}${chatId}`;

/** Reads every mark of this integration. Storage may throw (private mode): then there are none. */
export function loadReadMarks(storage: VisitorStorage, publicKey: string | null): ReadMarks {
  const out: Record<string, number> = {};
  try {
    const prefix = PREFIX(publicKey);
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key?.startsWith(prefix)) continue;
      const at = Number(storage.getItem(key));
      if (Number.isFinite(at) && at > 0) out[key.slice(prefix.length)] = at;
    }
  } catch {
    // Best-effort: no marks means the BE count stands.
  }
  return out;
}

/** Stores a mark; a lower one never replaces a higher one. */
export function saveReadMark(
  storage: VisitorStorage,
  publicKey: string | null,
  chatId: string,
  atMs: number,
): void {
  if (!chatId || !Number.isFinite(atMs)) return;
  try {
    const key = readMarkKey(publicKey, chatId);
    const prev = Number(storage.getItem(key));
    if (Number.isFinite(prev) && prev >= atMs) return;
    storage.setItem(key, String(atMs));
  } catch {
    // Private mode / full storage: the in-memory marks still hold for this mount.
  }
}

/** The row with its unread zeroed when its last message is at or before the thread's mark. */
export function applyReadMark<
  T extends { chatId: string; unreadCount: number; preview?: { at: string } | null },
>(item: T, marks: ReadMarks): T {
  const mark = marks[item.chatId];
  if (mark === undefined || item.unreadCount <= 0 || !item.preview) return item;
  const at = Date.parse(item.preview.at);
  if (Number.isNaN(at) || at > mark) return item;
  return { ...item, unreadCount: 0 };
}
